/**
 * ═══════════════════════════════════════════════════════════════════════════
 * SEARCH RESULTS PAGE - VERSION 5.2 DYNAMIC
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * LOGIQUE:
 *   1. Lire infoSource.allSettings.pages.productSearch → pageSettings
 *   2. Résolution — CASCADE STRICTE à 3 niveaux :
 *
 *      ┌─────────────────────────────────────────────────────────────────┐
 *      │  NIVEAU 1 — pageSettings.sections[]   (DB AnimationSettings)    │
 *      │  NIVEAU 2 — pageSettings.facets[]     (DB AnimationSettings)    │
 *      │  NIVEAU 3 — PAGES_CONFIG_.productSearch (statique pagesConfig)  │
 *      └─────────────────────────────────────────────────────────────────┘
 *
 *   SECTIONS INDÉPENDANTES (DataLoader propre):
 *     productGrid → facetKey: '__PRODUCTS_BASE__' → dataKey: 'products'
 *     → skip/limit gérés par SectionDataLoader dans DynamicPageRenderer
 *
 *   SECTIONS STANDARD (pageData global):
 *     historySearchMatrix, userViewMatrix, followShopUser, followMe, ...
 *
 *   PARAMÈTRES URL:
 *     ref → terme de recherche (passé à useDataLoader comme `search`)
 *     ci  → category id (passé à useDataLoader comme `ci`)
 *     → Le loader SEARCH (callSearchLoader) gère automatiquement:
 *       - getProductSearch
 *       - saveViewedHistory (historySearch LS / MongoDB)
 *       - mise à jour pageData.products / size / siseProduct
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import Layout from '../Layout/Layout';
import LoadingSwiper from '../slider/loadindSwiper';
import useDataLoader from '../pageInside/exeemple_page/DataLoader';
import DynamicPageRenderer from '../pageInside/exeemple_page/DynamicPageRenderer';
import { getInfoSourceUpdates } from '../pageInside/exeemple_page/allFacets';
import {
    PAGES_CONFIG_,
    SECTIONS_LIBRARY,
    LOADER_TYPES,
} from '../pageInside/exeemple_page/pagesConfig';
import { prices } from '../pageInside/fixedPrices';
import { isAuthenticated } from '../auth';
import { elakayAutoText } from "../pageInside/dataElakay";


// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTES MODULE
// ═══════════════════════════════════════════════════════════════════════════

const PAGE_ID = 'productSearch';

// ═══════════════════════════════════════════════════════════════════════════
// NIVEAU 3 — defaults depuis PAGES_CONFIG_.productSearch
// ═══════════════════════════════════════════════════════════════════════════

const DEFAULT_FACETS = (() => {
    const config = PAGES_CONFIG_[PAGE_ID];
    if (!config?.sections?.length) {
        return [
            'productGrid',
            'historySearchMatrix',
            'userViewMatrix',
            'followShopUser',
            'followMe',
        ];
    }
    return config.sections
        .filter(s => s.enabled !== false)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map(s => s.sectionId);
})();

const DEFAULT_CONFIG_OVERRIDES = (() => {
    const config = PAGES_CONFIG_[PAGE_ID];
    if (!config?.sections?.length) return {};

    const result = {};
    config.sections.forEach(s => {
        if (!s.sectionId) return;
        const entry = {};
        const lib   = SECTIONS_LIBRARY[s.sectionId];

        if (lib?.defaultProps || s.props) {
            entry.props = { ...(lib?.defaultProps || {}), ...(s.props || {}) };
        }
        if (s.overrides && Object.keys(s.overrides).length > 0) {
            entry.overrides = { ...s.overrides };
        }
        if (Object.keys(entry).length > 0) result[s.sectionId] = entry;
    });
    return result;
})();

// ═══════════════════════════════════════════════════════════════════════════
// useQuery — lecture des params URL
// ═══════════════════════════════════════════════════════════════════════════

const useQuery = () => new URLSearchParams(useLocation().search);

// ═══════════════════════════════════════════════════════════════════════════
// _extractFromSettings — NIVEAUX 1 & 2
// ═══════════════════════════════════════════════════════════════════════════

const _extractFromSettings = (settings) => {
    if (!settings || typeof settings !== 'object') return null;

    const { sections, facets } = settings;

    // ── Priorité 1 : sections[] ─────────────────────────────────────────────
    if (Array.isArray(sections) && sections.length > 0) {
        const sorted = [...sections]
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

        const resolvedFacets = sorted
            .filter(s => s.sectionId && s.enabled !== false)
            .map(s => s.sectionId);

        const configOverrides = {};
        sorted.forEach(s => {
            if (!s.sectionId) return;
            const entry = {};
            if (s.props     && Object.keys(s.props).length > 0)     entry.props     = s.props;
            if (s.overrides && Object.keys(s.overrides).length > 0) entry.overrides = s.overrides;
            if (Object.keys(entry).length > 0) configOverrides[s.sectionId] = entry;
        });

        if (resolvedFacets.length > 0) {
            return { facets: resolvedFacets, configOverrides };
        }
    }

    // ── Priorité 2 : facets[] ───────────────────────────────────────────────
    if (Array.isArray(facets) && facets.length > 0) {
        return { facets, configOverrides: {} };
    }

    return null;
};

// ═══════════════════════════════════════════════════════════════════════════
// resolvePageConfig — CASCADE 3 niveaux
// ═══════════════════════════════════════════════════════════════════════════

const resolvePageConfig = (pageSettings) => {
    const result = _extractFromSettings(pageSettings);
    if (result) return result;

    return {
        facets:          DEFAULT_FACETS,
        configOverrides: DEFAULT_CONFIG_OVERRIDES,
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT
// ═══════════════════════════════════════════════════════════════════════════

const ResutlsSearch = (props) => {
    const {
        infoSource,
        laguangeElakay,
        homeStyles,
    } = props;

    const token = infoSource?.token || null;

    // ─── Params URL ───────────────────────────────────────────────────────────
    const query  = useQuery();
    const search = query.get('ref') || '';
    const ci     = query.get('ci')  || null;

    // ─── Filtres ──────────────────────────────────────────────────────────────
    const [myFilters, setMyFilters] = useState(() => ({
        filters: {
            'categoriesID.gategoriesLink': [],
        },
    }));

    // filterShop conservé pour Layout (userShop pattern)
    const [filterShop, setFilterShop] = useState(false);

    const handlePrice = useCallback((value) => {
        // Slider "Customer price" → déjà un [min, max] numérique, pas un _id de palier.
        if (Array.isArray(value)) return value;
        let array = [];
        for (let key in prices) {
            if (prices[key]._id === parseInt(value)) {
                array = prices[key].array;
            }
        }
        return array;
    }, []);

    const handleFilters = useCallback((filters, filterBy) => {
        setMyFilters(prev => {
            const newFilters   = { ...prev };
            newFilters.filters = { ...prev.filters };

            if (filterBy === 'price') {
                newFilters.filters[filterBy] = handlePrice(filters);
            } else {
                newFilters.filters[filterBy] = filters;
            }

            return newFilters;
        });
    }, [handlePrice]);

    // ─── CASCADE ──────────────────────────────────────────────────────────────
    const pageSettings = infoSource?.allSettings?.pages?.[PAGE_ID];

    const { facets, configOverrides } = useMemo(
        () => resolvePageConfig(pageSettings),
        [
            JSON.stringify(pageSettings?.sections),
            JSON.stringify(pageSettings?.facets),
        ]
    );

    // ProductGridSearch owns its query and pagination. The page-level loader
    // only loads companion sections, otherwise two requests with different
    // limits race and overwrite the same metadata.
    const companionFacets = useMemo(
        () => facets.filter((facetId) => SECTIONS_LIBRARY[facetId]?.loaderType !== LOADER_TYPES.SEARCH),
        [facets]
    );

    // ─── DataLoader des sections compagnons uniquement ────────────────────
    // ProductGridSearch effectue l'unique requête Search avec sa limite
    // effective. Cela évite le conflit historique 15 (parent) / 12 (grille).

    const {
        pageData,
        loading,
        mergePageData,
        loadPageData,
        reloadFacets,
    } = useDataLoader({
        facets: companionFacets,
        infoSource,
        token,
        filters: myFilters.filters,
        search,
        ci,
    });

    const handleSearchData = useCallback((response) => {
        const products = Array.isArray(response?.products) ? response.products : [];
        mergePageData({
            searchCategories: response?.categories || [],
            searchFilters: response?.filters || {},
            searchOwner: response?.owner || null,
            size: response?.size ?? products.length,
            siseProduct: response?.totalProducts ?? response?.siseProduct ?? products.length,
        });
    }, [mergePageData]);

    const runtimeConfigOverrides = useMemo(() => {
        const next = { ...configOverrides };
        facets.forEach((facetId) => {
            if (SECTIONS_LIBRARY[facetId]?.loaderType !== LOADER_TYPES.SEARCH) return;
            const current = next[facetId] || {};
            next[facetId] = {
                ...current,
                props: {
                    ...(current.props || {}),
                    search,
                    ci,
                    token,
                    onSearchData: handleSearchData,
                },
            };
        });
        return next;
    }, [configOverrides, facets, search, ci, token, handleSearchData]);

    // ─── Sync infoSource → pageData (sections loaderType NONE) ───────────────
    useEffect(() => {
        const updates = getInfoSourceUpdates(companionFacets, infoSource);
        if (Object.keys(updates).length > 0) {
            mergePageData(updates);
        }
    }, [infoSource, companionFacets]);

    // ─── Recharger quand facets / filtres / search / ci changent ──────────────
    useEffect(() => {
        loadPageData();
    }, [
        JSON.stringify(companionFacets),
        JSON.stringify(myFilters.filters),
        search,
        ci,
    ]);

    // ─── getCalFilShop — hérité du v1, calculé depuis pageData ───────────────
    //
    // v1 construisait: { owner, categoies, filters, prices } depuis la réponse
    // search. Ici on le reconstruit depuis pageData qui contient les mêmes
    // données via le mapResponse de productSearch (searchOwner, searchCategories,
    // searchFilters).
    const getCalFilShop = useMemo(() => ({
        owner:     pageData?.searchOwner     || null,
        categoies: pageData?.searchCategories || [],
        filters:   pageData?.searchFilters   || {},
        prices,
    }), [pageData?.searchOwner, pageData?.searchCategories, pageData?.searchFilters]);

    // ─── P1 flag ──────────────────────────────────────────────────────────────
    const P1 = (pageData?.size || 0) > 0;

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <Layout
            title={infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).resultsSearch.t001}
            description={elakayAutoText("h_41682905073222", "Node React E-commerce App")}
            className="container-fluid"
            infoSource={infoSource}
            loading={loading}
            SliderPunShow={false}
            prices={prices}
            userShop={true}
            filterShop={filterShop}
            setFilterShop={setFilterShop}
            handleFilters={handleFilters}
            setMyFilters={setMyFilters}
            getCalFilShop={getCalFilShop}
            P1={P1}
            {...infoSource?.allSettings?.layout}
        >
            <div style={infoSource.styleNav}>
                <DynamicPageRenderer
                    facets={facets}
                    pageId={PAGE_ID}
                    pageData={pageData}
                    infoSource={infoSource}
                    laguangeElakay={laguangeElakay}
                    homeStyles={homeStyles}
                    reloadFacets={reloadFacets}
                    configOverrides={runtimeConfigOverrides}
                    // ── Pour sections indépendantes (productGrid) ────────────
                    token={token}
                    myFilters={myFilters}
                    search={search}
                    ci={ci}
                />

                {loading && !pageData?.products?.length && (
                    <>
                        <LoadingSwiper myStyles={homeStyles} />
                        <LoadingSwiper myStyles={homeStyles} />
                    </>
                )}
            </div>
        </Layout>
    );
};

export default ResutlsSearch;
